import {
    ArrowDownTrayIcon,
    ArrowUpTrayIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    EyeIcon,
    InformationCircleIcon,
    MagnifyingGlassIcon,
    PencilIcon,
    PhotoIcon,
    PlusIcon,
    TrashIcon,
    XCircleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useRef, useState } from "react";
import * as XLSX from 'xlsx';
import { ADMIN_CONFIG } from "../../config/adminConfig";
import { useAdmin } from "../../contexts/AdminContext";
import apiService, { adminService } from "../../services/api";

const ProductManagement = () => {
    const { refreshProducts, refreshKey } = useAdmin();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [formData, setFormData] = useState(null);
    const searchTimeoutRef = useRef(null);

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            const filters = {
                page: currentPage,
                limit: ADMIN_CONFIG.pagination.defaultLimit,
            };

            if (searchTerm && searchTerm.trim()) {
                filters.search = searchTerm.trim();
            }

            const response = await adminService.getAllProducts(filters);

            if (response.status === "success") {
                setProducts(response.data.products || []);
                setTotalPages(response.data.pagination?.totalPages || 1);
            }
        } catch (error) {
            console.error("Failed to fetch products:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm]);

    const fetchFormData = useCallback(async () => {
        try {
            // Clear cache for form-data to ensure we get fresh categories
            apiService.clearCacheForEndpoint("/admin/products/form-data");

            const response = await adminService.getProductFormData();
            if (response.status === "success") {
                setFormData(response.data);
            }
        } catch (error) {
            console.error("Failed to fetch form data:", error);
        }
    }, []);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts, refreshKey]);

    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setSearchTerm(searchInput);
            setCurrentPage(1);
        }, 500);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchInput]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    useEffect(() => {
        if (showModal) {
            // Always fetch fresh form data when modal opens to get latest categories
            fetchFormData();
        }
    }, [showModal, fetchFormData]);

    const handleDeleteProduct = async productId => {
        if (
            window.confirm("Are you sure you want to delete this product? This will also delete all associated images.")
        ) {
            try {
                await adminService.deleteProduct(productId);
                await refreshProducts();
                fetchProducts();
            } catch (error) {
                console.error("Failed to delete product:", error);
                alert("Failed to delete product");
            }
        }
    };

    const handleEditProduct = product => {
        setEditingProduct(product);
        setShowModal(true);
    };

    const handleCreateProduct = () => {
        setEditingProduct(null);
        setShowModal(true);
    };

    const handleDownloadTemplate = () => {
        // Sample data for the template
        const sampleData = [{
            name: "Business Cards Premium",
            description: "High-quality business cards",
            category: "business-cards",
            subcategory: "Premium Cards",
            isActive: true,
            isFeatured: false,
            paperTypes: JSON.stringify([{
                name: "Premium Cardstock",
                gsm: ADMIN_CONFIG.product.defaultGSM,
                finish: "matte",
                priceMultiplier: 1
            }]),
            sizes: JSON.stringify([{
                name: "Standard",
                dimensions: {
                    width: 89,
                    height: 51,
                    unit: "mm"
                },
                priceMultiplier: 1
            }]),
            finishOptions: JSON.stringify([{
                name: "Matte",
                description: "Smooth matte finish",
                priceMultiplier: 1
            }]),
            cornerOptions: JSON.stringify([{
                name: "Square",
                description: "Standard square corners",
                priceMultiplier: 1
            }]),
            pricing: JSON.stringify([{
                quantity: ADMIN_CONFIG.product.defaultQuantity,
                basePrice: ADMIN_CONFIG.product.defaultBasePrice,
                discountPrice: ADMIN_CONFIG.product.defaultDiscountPrice,
                isRecommended: false
            }]),
            features: "Premium quality, Fast delivery, Custom design",
            tags: "business, premium, cards"
        }];

        // Create a new workbook
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(sampleData);

        // Add column widths
        ws['!cols'] = [
            { wch: 20 }, // name
            { wch: 30 }, // description
            { wch: 15 }, // category
            { wch: 15 }, // subcategory
            { wch: 10 }, // isActive
            { wch: 10 }, // isFeatured
            { wch: 40 }, // paperTypes
            { wch: 40 }, // sizes
            { wch: 40 }, // finishOptions
            { wch: 40 }, // cornerOptions
            { wch: 40 }, // pricing
            { wch: 30 }, // features
            { wch: 20 }, // tags
        ];

        // Add the worksheet to the workbook
        XLSX.utils.book_append_sheet(wb, ws, "Products Template");

        // Save the file
        XLSX.writeFile(wb, "product_upload_template.xlsx");
    };

    const [uploadStatus, setUploadStatus] = useState(null);
    const [uploadResults, setUploadResults] = useState(null);
    const [showErrorDetails, setShowErrorDetails] = useState(false);

    const handleExcelUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel'
        ];
        if (!validTypes.includes(file.type)) {
            setUploadStatus({
                type: 'error',
                message: 'Invalid file type. Please upload an Excel file (.xlsx or .xls)'
            });
            event.target.value = '';
            return;
        }

        try {
            setLoading(true);
            setUploadStatus({
                type: 'info',
                message: 'Processing Excel file...'
            });

            const reader = new FileReader();

            reader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet);

                    // Validate data structure
                    if (jsonData.length === 0) {
                        throw new Error('Excel file is empty');
                    }

                    // Basic validation of required fields
                    const missingFields = jsonData.reduce((acc, row, index) => {
                        const missing = [];
                        if (!row.name) missing.push('name');
                        if (!row.description) missing.push('description');
                        if (!row.category) missing.push('category');
                        if (missing.length > 0) {
                            acc.push({ row: index + 2, fields: missing });
                        }
                        return acc;
                    }, []);

                    if (missingFields.length > 0) {
                        const errorMessage = missingFields.map(({ row, fields }) =>
                            `Row ${row}: Missing required fields (${fields.join(', ')})`
                        ).join('\\n');
                        throw new Error(errorMessage);
                    }

                    setUploadStatus({
                        type: 'info',
                        message: 'Uploading products...'
                    });

                    // Upload the parsed data to the server
                    const response = await adminService.uploadProductsExcel(jsonData);

                    if (response.status === 'success') {
                        const { results } = response.data;
                        setUploadResults(results);
                        setUploadStatus({
                            type: 'success',
                            message: `Successfully uploaded ${results.success.length} products${results.errors.length > 0 ? ` (${results.errors.length} failed)` : ''
                                }`
                        });
                        await refreshProducts();
                        fetchProducts();
                    } else {
                        throw new Error('Failed to upload products');
                    }
                } catch (error) {
                    console.error('Error processing Excel file:', error);
                    setUploadStatus({
                        type: 'error',
                        message: error.message || 'Error processing Excel file. Please check the format.'
                    });
                }
            };

            reader.onerror = () => {
                setUploadStatus({
                    type: 'error',
                    message: 'Error reading the Excel file'
                });
            };

            reader.readAsArrayBuffer(file);
        } catch (error) {
            console.error('Error uploading Excel file:', error);
            setUploadStatus({
                type: 'error',
                message: 'Error uploading Excel file'
            });
        } finally {
            setLoading(false);
            // Reset the file input
            event.target.value = '';
        }
    };

    if (loading && products.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-3 border-green-200 border-t-[#008000]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-6">
                <div className="space-y-2">
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-[#008000] to-emerald-600 bg-clip-text text-transparent">
                        Product Management
                    </h2>
                    <p className="text-gray-600 font-medium">Manage your product catalog with precision</p>
                </div>
                <div className="flex gap-4">
                    <button
                        onClick={handleCreateProduct}
                        className="bg-gradient-to-r from-[#008000] to-emerald-600 text-white px-6 py-3 rounded-xl hover:from-[#008000] hover:to-emerald-700 flex items-center gap-3 shadow-lg hover:shadow-xl transition-colors font-medium"
                    >
                        <PlusIcon className="h-5 w-5" />
                        Add New Product
                    </button>
                    <div className="flex gap-2">
                        <button
                            onClick={() => document.getElementById('excelUpload').click()}
                            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 flex items-center gap-3 shadow-lg hover:shadow-xl transition-colors font-medium"
                        >
                            <ArrowUpTrayIcon className="h-5 w-5" />
                            Upload Excel
                        </button>
                        <button
                            onClick={handleDownloadTemplate}
                            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl hover:from-purple-700 hover:to-pink-700 flex items-center gap-3 shadow-lg hover:shadow-xl transition-colors font-medium"
                        >
                            <ArrowDownTrayIcon className="h-5 w-5" />
                            Download Template
                        </button>
                        <input
                            type="file"
                            id="excelUpload"
                            accept=".xlsx,.xls"
                            className="hidden"
                            onChange={handleExcelUpload}
                        />
                    </div>
                </div>
            </div>

            {/* Upload Status */}
            {uploadStatus && (
                <div
                    className={`backdrop-blur-xl border rounded-2xl shadow-xl p-6 ${uploadStatus.type === 'success'
                        ? 'bg-green-50/80 border-green-100'
                        : uploadStatus.type === 'error'
                            ? 'bg-red-50/80 border-red-100'
                            : 'bg-blue-50/80 border-blue-100'
                        }`}
                >
                    <div className="flex items-start gap-4">
                        <div
                            className={`rounded-full p-2 ${uploadStatus.type === 'success'
                                ? 'bg-green-100 text-[#008000]'
                                : uploadStatus.type === 'error'
                                    ? 'bg-red-100 text-red-600'
                                    : 'bg-blue-100 text-blue-600'
                                }`}
                        >
                            {uploadStatus.type === 'success' ? (
                                <CheckCircleIcon className="h-6 w-6" />
                            ) : uploadStatus.type === 'error' ? (
                                <XCircleIcon className="h-6 w-6" />
                            ) : (
                                <InformationCircleIcon className="h-6 w-6" />
                            )}
                        </div>
                        <div className="flex-1">
                            <h3
                                className={`text-lg font-semibold ${uploadStatus.type === 'success'
                                    ? 'text-[#008000]'
                                    : uploadStatus.type === 'error'
                                        ? 'text-red-800'
                                        : 'text-blue-800'
                                    }`}
                            >
                                {uploadStatus.type === 'success'
                                    ? 'Upload Successful'
                                    : uploadStatus.type === 'error'
                                        ? 'Upload Failed'
                                        : 'Processing Upload'}
                            </h3>
                            <p
                                className={`mt-1 text-sm ${uploadStatus.type === 'success'
                                    ? 'text-[#008000]'
                                    : uploadStatus.type === 'error'
                                        ? 'text-red-700'
                                        : 'text-blue-700'
                                    }`}
                            >
                                {uploadStatus.message}
                            </p>
                            {uploadResults && uploadResults.errors.length > 0 && (
                                <div className="mt-4">
                                    <button
                                        onClick={() => setShowErrorDetails(!showErrorDetails)}
                                        className="text-sm font-medium text-red-700 hover:text-red-800 flex items-center gap-1"
                                    >
                                        {showErrorDetails ? (
                                            <ChevronUpIcon className="h-4 w-4" />
                                        ) : (
                                            <ChevronDownIcon className="h-4 w-4" />
                                        )}
                                        {uploadResults.errors.length} Error{uploadResults.errors.length !== 1 && 's'}
                                    </button>
                                    {showErrorDetails && (
                                        <div className="mt-2 space-y-2">
                                            {uploadResults.errors.map((error, index) => (
                                                <div
                                                    key={index}
                                                    className="text-sm text-red-700 bg-red-50 rounded-lg p-3 border border-red-100"
                                                >
                                                    <div className="font-medium">Row {index + 2}</div>
                                                    <div className="mt-1">{error.error}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => {
                                setUploadStatus(null);
                                setUploadResults(null);
                            }}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            )}

            {/* Search */}
            <div className="backdrop-blur-xl bg-white/80 border border-green-100 rounded-2xl shadow-xl p-6">
                <div className="relative group">
                    <MagnifyingGlassIcon className="h-5 w-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-green-500 group-focus-within:text-[#008000] transition-colors duration-200" />
                    <input
                        type="text"
                        placeholder="Search products..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                if (searchTimeoutRef.current) {
                                    clearTimeout(searchTimeoutRef.current);
                                }
                                setSearchTerm(searchInput);
                                setCurrentPage(1);
                            }
                        }}
                        className="w-full pl-12 pr-4 py-3 border-2 border-green-100 rounded-xl focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 bg-white/90 backdrop-blur-sm text-sm"
                    />
                </div>
            </div>

            {/* Products Table - Desktop */}
            <div className="hidden lg:block backdrop-blur-xl bg-white/80 border border-green-100 rounded-2xl shadow-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
                            <tr>
                                <th className="px-6 py-4 text-left text-sm font-bold text-[#008000] uppercase tracking-wider">
                                    Product
                                </th>
                                <th className="px-6 py-4 text-left text-sm font-bold text-[#008000] uppercase tracking-wider">
                                    Category
                                </th>
                                <th className="px-6 py-4 text-left text-sm font-bold text-[#008000] uppercase tracking-wider">
                                    Price Range
                                </th>
                                <th className="px-6 py-4 text-left text-sm font-bold text-[#008000] uppercase tracking-wider">
                                    Stock Status
                                </th>
                                <th className="px-6 py-4 text-left text-sm font-bold text-[#008000] uppercase tracking-wider">
                                    Analytics
                                </th>
                                <th className="px-6 py-4 text-left text-sm font-bold text-[#008000] uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-4 text-left text-sm font-bold text-[#008000] uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white/60 backdrop-blur-sm divide-y divide-green-100">
                            {products.map((product) => (
                                <tr
                                    key={product._id}
                                    className="hover:bg-green-50/50 transition-colors"
                                >
                                    <td className="px-6 py-5 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="h-14 w-14 flex-shrink-0 aspect-square">
                                                <img
                                                    className="h-full w-full rounded-xl object-cover border-2 border-green-100 shadow-sm"
                                                    src={product.images?.[0]?.url || "/placeholder.svg"}
                                                    alt={product.name}
                                                />
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-bold text-gray-900">
                                                    {product.name}
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    {product.description?.substring(0, 60)}...
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap">
                                        <span className="inline-flex px-3 py-1 text-xs font-bold rounded-full bg-gradient-to-r from-green-100 to-emerald-100 text-[#008000] border border-green-200">
                                            {product.category}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-900">
                                        {product.pricing && product.pricing.length > 0 ? (
                                            <div>
                                                <div className="font-bold text-[#008000]">
                                                    ₹
                                                    {Math.min(
                                                        ...product.pricing.map(p => p.discountPrice || p.basePrice)
                                                    )}{" "}
                                                    - ₹
                                                    {Math.max(
                                                        ...product.pricing.map(p => p.discountPrice || p.basePrice)
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-500 font-medium">
                                                    {product.pricing.length} tier
                                                    {product.pricing.length !== 1 ? "s" : ""}
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 font-medium">No pricing set</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-900">
                                        {product.stock?.isTrackable ? (
                                            <div>
                                                <span
                                                    className={`font-bold ${product.stock.quantity <= product.stock.lowStockThreshold
                                                        ? "text-red-600"
                                                        : "text-[#008000]"
                                                        }`}
                                                >
                                                    {product.stock.quantity} units
                                                </span>
                                                {product.stock.quantity <= product.stock.lowStockThreshold && (
                                                    <div className="text-xs text-red-500 font-medium">
                                                        ⚠ Low stock
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-blue-600 font-medium">Not tracked</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-600">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2">
                                                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                                <span className="font-medium">
                                                    Views: {product.analytics?.views || 0}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                                <span className="font-medium">
                                                    Orders: {product.analytics?.orders || 0}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                                                <span className="font-medium">
                                                    Revenue: ₹{product.analytics?.revenue || 0}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap">
                                        <span
                                            className={`inline-flex px-3 py-1 text-xs font-bold rounded-full border ${product.isActive
                                                ? "bg-gradient-to-r from-green-100 to-emerald-100 text-[#008000] border-green-200"
                                                : "bg-gradient-to-r from-red-100 to-pink-100 text-red-800 border-red-200"
                                                }`}
                                        >
                                            {product.isActive ? "✓ Active" : "✗ Inactive"}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5 whitespace-nowrap text-sm font-medium">
                                        <div className="flex space-x-3">
                                            <button
                                                onClick={() => window.open(`/product/${product._id}`, "_blank")}
                                                className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                                            >
                                                <EyeIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleEditProduct(product)}
                                                className="text-amber-600 hover:text-amber-800 p-2 rounded-lg hover:bg-amber-50 transition-colors"
                                            >
                                                <PencilIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteProduct(product._id)}
                                                className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-white/90 backdrop-blur-sm px-6 py-4 flex items-center justify-between border-t border-green-100">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center px-4 py-2 border-2 border-green-200 text-sm font-medium rounded-xl text-[#008000] bg-white/90 hover:bg-green-50 disabled:opacity-50 transition-colors"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border-2 border-green-200 text-sm font-medium rounded-xl text-[#008000] bg-white/90 hover:bg-green-50 disabled:opacity-50 transition-colors"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700 font-medium">
                                    Page <span className="font-bold text-[#008000]">{currentPage}</span> of{" "}
                                    <span className="font-bold text-[#008000]">{totalPages}</span>
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-xl shadow-sm -space-x-px">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-bold transition-colors ${page === currentPage
                                                ? "z-10 bg-gradient-to-r from-green-500 to-emerald-500 border-green-500 text-white shadow-lg"
                                                : "bg-white/90 border-green-200 text-[#008000] hover:bg-green-50"
                                                } ${page === 1 ? "rounded-l-xl" : ""} ${page === totalPages ? "rounded-r-xl" : ""
                                                }`}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Product Modal */}
            {showModal && (
                <ProductModal
                    product={editingProduct}
                    formData={formData}
                    onClose={() => setShowModal(false)}
                    onSave={async () => {
                        setShowModal(false);
                        await refreshProducts();
                        fetchProducts();
                    }}
                />
            )}
        </div>
    );
};

// Product Modal Component
const ProductModal = ({ product, formData, onClose, onSave }) => {
    const [formFields, setFormFields] = useState({
        name: product?.name || "",
        description: product?.description || "",
        category: product?.category || "",
        subcategory: product?.subcategory || "",
        features: product?.features || [],
        tags: product?.tags || [],
        isActive: product?.isActive ?? true,
        isFeatured: product?.isFeatured ?? false,
    });

    const [specifications, setSpecifications] = useState({
        paperTypes: product?.specifications?.paperTypes || [],
        sizes: product?.specifications?.sizes || [],
        finishOptions: product?.specifications?.finishOptions || [],
        cornerOptions: product?.specifications?.cornerOptions || [],
    });

    const [pricing, setPricing] = useState(() => {
        if (product?.pricing && product.pricing.length > 0) {
            return product.pricing.map(tier => ({
                quantity: tier.quantity || "",
                basePrice: tier.basePrice || "",
                discountPrice: tier.discountPrice || "",
                isRecommended: tier.isRecommended || false,
                specificationPrices: tier.specificationPrices || {
                    paperTypes: {},
                    sizes: {},
                    finishOptions: {},
                    cornerOptions: {}
                },
                deliveryInfo: tier.deliveryInfo || {
                    standardDelivery: {
                        days: 3,
                        cost: 0
                    },
                    expressDelivery: {
                        days: 1,
                        cost: 50,
                        isAvailable: true
                    }
                }
            }));
        }
        return [{
            quantity: "",
            basePrice: "",
            discountPrice: "",
            isRecommended: false,
            specificationPrices: {
                paperTypes: {},
                sizes: {},
                finishOptions: {},
                cornerOptions: {}
            },
            deliveryInfo: {
                standardDelivery: {
                    days: 3,
                    cost: 0
                },
                expressDelivery: {
                    days: 1,
                    cost: 50,
                    isAvailable: true
                }
            }
        }];
    });

    const [expandedTiers, setExpandedTiers] = useState({});

    const [images, setImages] = useState([]);
    const [existingImages, setExistingImages] = useState(product?.images || []);
    const [imagesToDelete, setImagesToDelete] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Update existing images when product changes
    useEffect(() => {
        if (product?.images) {
            setExistingImages(product.images);
        }
    }, [product]);

    const handleInputChange = (field, value) => {
        setFormFields(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleSpecificationChange = (type, index, field, value) => {
        setSpecifications(prev => ({
            ...prev,
            [type]: prev[type].map((item, i) => (i === index ? { ...item, [field]: value } : item)),
        }));
    };

    const handleDimensionChange = (index, field, value) => {
        setSpecifications(prev => ({
            ...prev,
            sizes: prev.sizes.map((size, i) =>
                i === index ? { ...size, dimensions: { ...size.dimensions, [field]: value } } : size
            ),
        }));
    };

    const addSpecificationItem = type => {
        const newItems = {
            paperTypes: { name: "", gsm: "", finish: "matte" },
            sizes: { name: "", dimensions: { width: "", height: "", unit: "mm" } },
            finishOptions: { name: "", description: "" },
            cornerOptions: { name: "", description: "" },
        };

        setSpecifications(prev => ({
            ...prev,
            [type]: [...prev[type], newItems[type]],
        }));
    };

    const removeSpecificationItem = (type, index) => {
        setSpecifications(prev => ({
            ...prev,
            [type]: prev[type].filter((_, i) => i !== index),
        }));
    };

    const handlePricingChange = (index, field, value) => {
        setPricing(prev => prev.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier)));
    };

    const handleSpecificationPriceChange = (tierIndex, specType, specName, price) => {
        setPricing(prev => prev.map((tier, i) => {
            if (i === tierIndex) {
                const newSpecPrices = { ...tier.specificationPrices };
                newSpecPrices[specType] = {
                    ...newSpecPrices[specType],
                    [specName]: parseFloat(price) || 0
                };
                return { ...tier, specificationPrices: newSpecPrices };
            }
            return tier;
        }));
    };

    const handleTierDeliveryChange = (tierIndex, deliveryType, field, value) => {
        setPricing(prev => prev.map((tier, i) => {
            if (i === tierIndex) {
                const newDeliveryInfo = { ...tier.deliveryInfo };
                newDeliveryInfo[deliveryType] = {
                    ...newDeliveryInfo[deliveryType],
                    [field]: value
                };
                return { ...tier, deliveryInfo: newDeliveryInfo };
            }
            return tier;
        }));
    };

    const toggleTierExpansion = (tierIndex) => {
        setExpandedTiers(prev => ({
            ...prev,
            [tierIndex]: !prev[tierIndex]
        }));
    };

    const addPricingTier = () => {
        setPricing(prev => [...prev, {
            quantity: "",
            basePrice: "",
            discountPrice: "",
            isRecommended: false,
            specificationPrices: {
                paperTypes: {},
                sizes: {},
                finishOptions: {},
                cornerOptions: {}
            },
            deliveryInfo: {
                standardDelivery: {
                    days: 3,
                    cost: 0
                },
                expressDelivery: {
                    days: 1,
                    cost: 50,
                    isAvailable: true
                }
            }
        }]);
    };

    const removePricingTier = index => {
        setPricing(prev => prev.filter((_, i) => i !== index));
    };

    const handleImageUpload = event => {
        const files = Array.from(event.target.files);
        if (files.length + images.length + existingImages.length - imagesToDelete.length > 10) {
            alert("Maximum 10 images allowed");
            return;
        }
        setImages(prev => [...prev, ...files]);
    };

    const removeNewImage = index => {
        setImages(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingImage = (image) => {
        // Use public_id if available, otherwise fallback to url
        const identifier = image.public_id || image.url;
        setImagesToDelete(prev => {
            if (!prev.includes(identifier)) {
                return [...prev, identifier];
            }
            return prev;
        });
    };

    const restoreExistingImage = (image) => {
        // Use public_id if available, otherwise fallback to url
        const identifier = image.public_id || image.url;
        setImagesToDelete(prev => prev.filter(id => id !== identifier));
    };

    const handleFeatureChange = (index, value) => {
        setFormFields(prev => ({
            ...prev,
            features: prev.features.map((feature, i) => (i === index ? value : feature)),
        }));
    };

    const addFeature = () => {
        setFormFields(prev => ({
            ...prev,
            features: [...prev.features, ""],
        }));
    };

    const removeFeature = index => {
        setFormFields(prev => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index),
        }));
    };

    const handleTagsChange = value => {
        const tags = value
            .split(",")
            .map(tag => tag.trim())
            .filter(tag => tag);
        setFormFields(prev => ({ ...prev, tags }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formFields.name.trim()) newErrors.name = "Product name is required";
        if (!formFields.description.trim()) newErrors.description = "Description is required";
        if (!formFields.category) newErrors.category = "Category is required";
        if (!formFields.subcategory.trim()) newErrors.subcategory = "Subcategory is required";

        // Validate specifications - paper types and sizes are optional

        // Validate finishOptions - ensure all entries have names if any exist
        const finishOptionsWithContent = specifications.finishOptions.filter(
            fo => fo.name.trim() || fo.description.trim()
        );
        if (finishOptionsWithContent.length > 0 && !finishOptionsWithContent.every(fo => fo.name.trim())) {
            newErrors.finishOptions = "All finish options must have a name";
        }

        // Validate cornerOptions - ensure all entries have names if any exist
        const cornerOptionsWithContent = specifications.cornerOptions.filter(
            co => co.name.trim() || co.description.trim()
        );
        if (cornerOptionsWithContent.length > 0 && !cornerOptionsWithContent.every(co => co.name.trim())) {
            newErrors.cornerOptions = "All corner options must have a name";
        }

        // Validate pricing
        if (!pricing.some(p => p.quantity && p.basePrice)) {
            newErrors.pricing = "At least one pricing tier is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async e => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const formDataToSend = new FormData();

            // Add text fields
            formDataToSend.append("name", formFields.name);
            formDataToSend.append("description", formFields.description);
            formDataToSend.append("category", formFields.category);
            formDataToSend.append("subcategory", formFields.subcategory);
            formDataToSend.append("isActive", formFields.isActive);
            formDataToSend.append("isFeatured", formFields.isFeatured);

            // Clean up specifications before sending
            const cleanedSpecifications = {
                paperTypes: specifications.paperTypes.filter(pt => pt.name.trim()),
                sizes: specifications.sizes.filter(s => s.name.trim()),
                finishOptions: specifications.finishOptions.filter(fo => fo.name.trim()),
                cornerOptions: specifications.cornerOptions.filter(co => co.name.trim()),
            };

            // Add specifications as JSON
            formDataToSend.append("specifications", JSON.stringify(cleanedSpecifications));

            // Process pricing tiers - convert specificationPrices Maps to objects
            const processedPricing = pricing.filter(p => p.quantity && p.basePrice).map(tier => {
                const processedTier = { ...tier };
                // Convert Maps to plain objects for JSON serialization
                if (processedTier.specificationPrices) {
                    processedTier.specificationPrices = {
                        paperTypes: processedTier.specificationPrices.paperTypes instanceof Map
                            ? Object.fromEntries(processedTier.specificationPrices.paperTypes)
                            : processedTier.specificationPrices.paperTypes || {},
                        sizes: processedTier.specificationPrices.sizes instanceof Map
                            ? Object.fromEntries(processedTier.specificationPrices.sizes)
                            : processedTier.specificationPrices.sizes || {},
                        finishOptions: processedTier.specificationPrices.finishOptions instanceof Map
                            ? Object.fromEntries(processedTier.specificationPrices.finishOptions)
                            : processedTier.specificationPrices.finishOptions || {},
                        cornerOptions: processedTier.specificationPrices.cornerOptions instanceof Map
                            ? Object.fromEntries(processedTier.specificationPrices.cornerOptions)
                            : processedTier.specificationPrices.cornerOptions || {}
                    };
                }
                return processedTier;
            });

            // Add pricing as JSON
            formDataToSend.append("pricing", JSON.stringify(processedPricing));

            // Add features as JSON
            if (formFields.features.length > 0) {
                formDataToSend.append("features", JSON.stringify(formFields.features.filter(f => f.trim())));
            }

            // Add tags as JSON
            if (formFields.tags.length > 0) {
                formDataToSend.append("tags", JSON.stringify(formFields.tags));
            }

            // Add new images
            images.forEach(image => {
                formDataToSend.append("images", image);
            });

            // Add images to delete (for updates)
            if (product && imagesToDelete.length > 0) {
                imagesToDelete.forEach(publicId => {
                    formDataToSend.append("imagesToDelete", publicId);
                });
            }

            if (product) {
                await adminService.updateProductWithImages(product._id, formDataToSend);
            } else {
                await adminService.createProductWithImages(formDataToSend);
            }

            onSave();
        } catch (error) {
            console.error("Failed to save product:", error);
            alert("Failed to save product: " + (error.message || "Unknown error"));
        } finally {
            setLoading(false);
        }
    };

    if (!formData) {
        return (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center">
                <div className="relative p-8 border border-green-200 w-96 shadow-2xl rounded-2xl bg-white/90 backdrop-blur-xl">
                    <div className="flex flex-col items-center justify-center h-32 space-y-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-3 border-green-200 border-t-[#008000]"></div>
                        <p className="text-[#008000] font-medium">Loading form data...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
            <div className="relative top-2 mx-auto p-4 sm:p-6 border border-green-200 max-w-7xl shadow-2xl rounded-2xl bg-white/95 backdrop-blur-xl mb-4 max-h-[95vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <div className="space-y-2">
                        <h3 className="text-2xl font-bold bg-gradient-to-r from-[#008000] to-emerald-600 bg-clip-text text-transparent">
                            {product ? "Edit Product" : "Create New Product"}
                        </h3>
                        <p className="text-gray-600 font-medium">
                            {product
                                ? "Update product information and specifications"
                                : "Add a new product to your catalog"}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 p-2 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Basic Information */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-gray-700">Product Name *</label>
                            <input
                                type="text"
                                value={formFields.name}
                                onChange={e => handleInputChange("name", e.target.value)}
                                className={`w-full border-2 rounded-xl px-4 py-3 transition-all duration-200 ${errors.name
                                    ? "border-red-300 focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                                    : "border-green-100 focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                                    } bg-white/90 backdrop-blur-sm`}
                                placeholder="Enter product name"
                                required
                            />
                            {errors.name && <p className="text-red-500 text-sm font-medium">{errors.name}</p>}
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-gray-700">Category *</label>
                            <select
                                value={formFields.category}
                                onChange={e => {
                                    handleInputChange("category", e.target.value);
                                    handleInputChange("subcategory", ""); // reset subcategory on category change
                                }}
                                className={`w-full border-2 rounded-xl px-4 py-3 transition-all duration-200 ${errors.category
                                    ? "border-red-300 focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                                    : "border-green-100 focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                                    } bg-white/90 backdrop-blur-sm`}
                                required
                            >
                                <option value="">Select Category</option>
                                {formData.categories?.map(category => (
                                    <option key={category} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </select>
                            {errors.category && <p className="text-red-500 text-sm font-medium">{errors.category}</p>}
                        </div>

                        <div className="lg:col-span-2 space-y-2">
                            <label className="block text-sm font-bold text-gray-700">Description *</label>
                            <textarea
                                value={formFields.description}
                                onChange={e => handleInputChange("description", e.target.value)}
                                className={`w-full border-2 rounded-xl px-4 py-3 transition-all duration-200 ${errors.description
                                    ? "border-red-300 focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                                    : "border-green-100 focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                                    } bg-white/90 backdrop-blur-sm resize-none`}
                                rows="3"
                                placeholder="Enter detailed product description"
                                required
                            />
                            {errors.description && (
                                <p className="text-red-500 text-sm font-medium">{errors.description}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-gray-700">Subcategory *</label>
                            {(() => {
                                const availableSubcategories = formFields.category
                                    ? formData?.categorySubcategoriesMap?.[formFields.category] || []
                                    : [];
                                const hasSubcategories = availableSubcategories.length > 0;

                                if (hasSubcategories) {
                                    return (
                                        <>
                                            <select
                                                value={formFields.subcategory}
                                                onChange={e => handleInputChange("subcategory", e.target.value)}
                                                className={`w-full border-2 rounded-xl px-4 py-3 transition-all duration-200 ${errors.subcategory
                                                    ? "border-red-300 focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                                                    : "border-green-100 focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                                                    } bg-white/90 backdrop-blur-sm`}
                                                required
                                            >
                                                <option value="">Select Subcategory</option>
                                                {availableSubcategories.map(sub => (
                                                    <option key={sub} value={sub}>{sub}</option>
                                                ))}
                                            </select>
                                            {errors.subcategory && (
                                                <p className="text-red-500 text-sm font-medium">{errors.subcategory}</p>
                                            )}
                                        </>
                                    );
                                }

                                return (
                                    <>
                                        <input
                                            type="text"
                                            value=""
                                            disabled
                                            className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 bg-gray-100 text-gray-400 cursor-not-allowed"
                                            placeholder={formFields.category ? "No subcategories defined for this category" : "Select a category first"}
                                        />
                                        {formFields.category && !hasSubcategories && (
                                            <p className="text-xs text-amber-600 font-medium">⚠ Add subcategories to this category in Category Management first.</p>
                                        )}
                                    </>
                                );
                            })()}
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-gray-700">Tags</label>
                            <input
                                type="text"
                                value={formFields.tags.join(", ")}
                                onChange={e => handleTagsChange(e.target.value)}
                                placeholder="business cards, marketing, premium"
                                className="w-full border-2 border-green-100 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 bg-white/90 backdrop-blur-sm"
                            />
                            <p className="text-xs text-gray-500">Separate tags with commas</p>
                        </div>
                    </div>

                    {/* Image Upload */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Product Images</label>
                        <div className="space-y-4">
                            {/* Existing Images */}
                            {existingImages.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium text-gray-600 mb-2">Current Images</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                        {existingImages.map((image, index) => {
                                            // Use public_id if available, otherwise fallback to url for identification
                                            const imageId = image.public_id || image.url;
                                            const isMarkedForDeletion = imagesToDelete.includes(imageId);

                                            return (
                                                <div key={imageId || index} className="relative aspect-square">
                                                    <img
                                                        src={image.url}
                                                        alt="Product"
                                                        className={`w-full h-full object-cover rounded border ${isMarkedForDeletion ? "opacity-50 border-red-300" : "border-gray-200"
                                                            }`}
                                                    />
                                                    {isMarkedForDeletion ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => restoreExistingImage(image)}
                                                            className="absolute top-1 right-1 bg-green-500 text-white rounded-full p-1 text-xs hover:bg-[#008000] transition-colors"
                                                            title="Restore image"
                                                        >
                                                            Restore
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeExistingImage(image)}
                                                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                                            title="Delete image"
                                                        >
                                                            <XMarkIcon className="h-3 w-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* New Images */}
                            {images.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium text-gray-600 mb-2">New Images</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                        {images.map((file, index) => (
                                            <div key={index} className="relative aspect-square">
                                                <img
                                                    src={URL.createObjectURL(file)}
                                                    alt="Preview"
                                                    className="w-full h-full object-cover rounded border"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeNewImage(index)}
                                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                                                >
                                                    <XMarkIcon className="h-3 w-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Upload Button */}
                            <div className="flex items-center justify-center w-full">
                                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                        <PhotoIcon className="w-8 h-8 mb-4 text-gray-500" />
                                        <p className="mb-2 text-sm text-gray-500">
                                            <span className="font-semibold">Click to upload</span> or drag and drop
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            PNG, JPG, WebP up to 10MB (Max 10 files)
                                        </p>
                                    </div>
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="hidden"
                                    />
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Specifications */}
                    <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-4">Specifications</h4>

                        {/* Paper Types */}
                        <div className="mb-6">
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium text-gray-700">Paper Types</label>
                                <button
                                    type="button"
                                    onClick={() => addSpecificationItem("paperTypes")}
                                    className="text-sm text-indigo-600 hover:text-indigo-800"
                                >
                                    + Add Paper Type
                                </button>
                            </div>
                            {errors.paperTypes && <p className="text-red-500 text-xs mb-2">{errors.paperTypes}</p>}
                            <div className="space-y-2">
                                {specifications.paperTypes.map((paperType, index) => (
                                    <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                                        <input
                                            type="text"
                                            placeholder="Name"
                                            value={paperType.name}
                                            onChange={e =>
                                                handleSpecificationChange("paperTypes", index, "name", e.target.value)
                                            }
                                            className="border border-gray-300 rounded-md px-3 py-2"
                                        />
                                        <input
                                            type="number"
                                            placeholder="GSM"
                                            value={paperType.gsm}
                                            onChange={e =>
                                                handleSpecificationChange("paperTypes", index, "gsm", e.target.value)
                                            }
                                            className="border border-gray-300 rounded-md px-3 py-2"
                                        />
                                        <select
                                            value={paperType.finish}
                                            onChange={e =>
                                                handleSpecificationChange("paperTypes", index, "finish", e.target.value)
                                            }
                                            className="border border-gray-300 rounded-md px-3 py-2"
                                        >
                                            {formData.commonFinishes?.map(finish => (
                                                <option key={finish} value={finish}>
                                                    {finish}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={() => removeSpecificationItem("paperTypes", index)}
                                            className="bg-red-500 text-white px-3 py-2 rounded-md hover:bg-red-600 transition-colors"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Sizes */}
                        <div className="mb-6">
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium text-gray-700">Sizes</label>
                                <button
                                    type="button"
                                    onClick={() => addSpecificationItem("sizes")}
                                    className="text-sm text-indigo-600 hover:text-indigo-800"
                                >
                                    + Add Size
                                </button>
                            </div>
                            {errors.sizes && <p className="text-red-500 text-xs mb-2">{errors.sizes}</p>}
                            <div className="space-y-2">
                                {specifications.sizes.map((size, index) => (
                                    <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
                                        <input
                                            type="text"
                                            placeholder="Size Name"
                                            value={size.name}
                                            onChange={e =>
                                                handleSpecificationChange("sizes", index, "name", e.target.value)
                                            }
                                            className="border border-gray-300 rounded-md px-3 py-2"
                                        />
                                        <input
                                            type="number"
                                            placeholder="Width"
                                            value={size.dimensions.width}
                                            onChange={e => handleDimensionChange(index, "width", e.target.value)}
                                            className="border border-gray-300 rounded-md px-3 py-2"
                                        />
                                        <input
                                            type="number"
                                            placeholder="Height"
                                            value={size.dimensions.height}
                                            onChange={e => handleDimensionChange(index, "height", e.target.value)}
                                            className="border border-gray-300 rounded-md px-3 py-2"
                                        />
                                        <select
                                            value={size.dimensions.unit}
                                            onChange={e => handleDimensionChange(index, "unit", e.target.value)}
                                            className="border border-gray-300 rounded-md px-3 py-2"
                                        >
                                            <option value="mm">mm</option>
                                            <option value="cm">cm</option>
                                            <option value="inch">inch</option>
                                        </select>
                                        <button
                                            type="button"
                                            onClick={() => removeSpecificationItem("sizes", index)}
                                            className="bg-red-500 text-white px-3 py-2 rounded-md hover:bg-red-600 transition-colors"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Finish Options */}
                        <div className="mb-6">
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium text-gray-700">Finish Options</label>
                                <button
                                    type="button"
                                    onClick={() => addSpecificationItem("finishOptions")}
                                    className="text-sm text-indigo-600 hover:text-indigo-800"
                                >
                                    + Add Finish Option
                                </button>
                            </div>
                            {errors.finishOptions && (
                                <p className="text-red-500 text-xs mb-2">{errors.finishOptions}</p>
                            )}
                            <div className="space-y-2">
                                {specifications.finishOptions.map((finish, index) => (
                                    <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                                        <input
                                            type="text"
                                            placeholder="Finish Name (e.g., Matte, Gloss)"
                                            value={finish.name}
                                            onChange={e =>
                                                handleSpecificationChange(
                                                    "finishOptions",
                                                    index,
                                                    "name",
                                                    e.target.value
                                                )
                                            }
                                            className="border border-gray-300 rounded-md px-3 py-2"
                                        />
                                        <input
                                            type="text"
                                            placeholder="Description"
                                            value={finish.description}
                                            onChange={e =>
                                                handleSpecificationChange(
                                                    "finishOptions",
                                                    index,
                                                    "description",
                                                    e.target.value
                                                )
                                            }
                                            className="border border-gray-300 rounded-md px-3 py-2"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeSpecificationItem("finishOptions", index)}
                                            className="bg-red-500 text-white px-3 py-2 rounded-md hover:bg-red-600 transition-colors"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Corner Options */}
                        <div className="mb-6">
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium text-gray-700">Corner Options</label>
                                <button
                                    type="button"
                                    onClick={() => addSpecificationItem("cornerOptions")}
                                    className="text-sm text-indigo-600 hover:text-indigo-800"
                                >
                                    + Add Corner Option
                                </button>
                            </div>
                            {errors.cornerOptions && (
                                <p className="text-red-500 text-xs mb-2">{errors.cornerOptions}</p>
                            )}
                            <div className="space-y-2">
                                {specifications.cornerOptions.map((corner, index) => (
                                    <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                                        <input
                                            type="text"
                                            placeholder="Corner Name (e.g., Square, Rounded)"
                                            value={corner.name}
                                            onChange={e =>
                                                handleSpecificationChange(
                                                    "cornerOptions",
                                                    index,
                                                    "name",
                                                    e.target.value
                                                )
                                            }
                                            className="border border-gray-300 rounded-md px-3 py-2"
                                        />
                                        <input
                                            type="text"
                                            placeholder="Description"
                                            value={corner.description}
                                            onChange={e =>
                                                handleSpecificationChange(
                                                    "cornerOptions",
                                                    index,
                                                    "description",
                                                    e.target.value
                                                )
                                            }
                                            className="border border-gray-300 rounded-md px-3 py-2"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeSpecificationItem("cornerOptions", index)}
                                            className="bg-red-500 text-white px-3 py-2 rounded-md hover:bg-red-600 transition-colors"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Pricing */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <h4 className="text-lg font-medium text-gray-900">Pricing Tiers *</h4>
                            <button
                                type="button"
                                onClick={addPricingTier}
                                className="text-sm text-indigo-600 hover:text-indigo-800"
                            >
                                + Add Pricing Tier
                            </button>
                        </div>
                        {errors.pricing && <p className="text-red-500 text-xs mb-2">{errors.pricing}</p>}
                        <div className="space-y-4">
                            {pricing.map((tier, tierIndex) => (
                                <div key={tierIndex} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end flex-1 mr-4">
                                            <input
                                                type="number"
                                                placeholder="Quantity"
                                                value={tier.quantity}
                                                onChange={e =>
                                                    handlePricingChange(tierIndex, "quantity", parseInt(e.target.value) || "")
                                                }
                                                className="border border-gray-300 rounded-md px-3 py-2"
                                            />
                                            <input
                                                type="number"
                                                step="0.01"
                                                placeholder="Base Price"
                                                value={tier.basePrice}
                                                onChange={e =>
                                                    handlePricingChange(tierIndex, "basePrice", parseFloat(e.target.value) || "")
                                                }
                                                className="border border-gray-300 rounded-md px-3 py-2"
                                            />
                                            <input
                                                type="number"
                                                step="0.01"
                                                placeholder="Discount Price"
                                                value={tier.discountPrice}
                                                onChange={e =>
                                                    handlePricingChange(
                                                        tierIndex,
                                                        "discountPrice",
                                                        parseFloat(e.target.value) || ""
                                                    )
                                                }
                                                className="border border-gray-300 rounded-md px-3 py-2"
                                            />
                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={tier.isRecommended}
                                                    onChange={e =>
                                                        handlePricingChange(tierIndex, "isRecommended", e.target.checked)
                                                    }
                                                    className="rounded border-gray-300"
                                                />
                                                <span className="ml-2 text-sm">Recommended</span>
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => removePricingTier(tierIndex)}
                                                disabled={pricing.length === 1}
                                                className="bg-red-500 text-white px-3 py-2 rounded-md disabled:opacity-50"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => toggleTierExpansion(tierIndex)}
                                            className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                                        >
                                            {expandedTiers[tierIndex] ? (
                                                <>
                                                    <ChevronUpIcon className="h-4 w-4" />
                                                    Hide Details
                                                </>
                                            ) : (
                                                <>
                                                    <ChevronDownIcon className="h-4 w-4" />
                                                    Show Details
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {expandedTiers[tierIndex] && (
                                        <div className="mt-4 space-y-6 border-t border-gray-200 pt-4">
                                            {/* Specification Prices */}
                                            <div>
                                                <h5 className="text-sm font-bold text-gray-700 mb-3">Specification Prices</h5>

                                                {/* Paper Types Prices */}
                                                {specifications.paperTypes.length > 0 && (
                                                    <div className="mb-4">
                                                        <label className="block text-xs font-medium text-gray-600 mb-2">Paper Types</label>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                            {specifications.paperTypes.map((paperType) => (
                                                                <div key={paperType.name} className="flex items-center gap-2">
                                                                    <span className="text-sm text-gray-700 w-32 truncate">{paperType.name}</span>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        placeholder="Price"
                                                                        value={tier.specificationPrices?.paperTypes?.[paperType.name] || 0}
                                                                        onChange={e =>
                                                                            handleSpecificationPriceChange(
                                                                                tierIndex,
                                                                                "paperTypes",
                                                                                paperType.name,
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Sizes Prices */}
                                                {specifications.sizes.length > 0 && (
                                                    <div className="mb-4">
                                                        <label className="block text-xs font-medium text-gray-600 mb-2">Sizes</label>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                            {specifications.sizes.map((size) => (
                                                                <div key={size.name} className="flex items-center gap-2">
                                                                    <span className="text-sm text-gray-700 w-32 truncate">{size.name}</span>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        placeholder="Price"
                                                                        value={tier.specificationPrices?.sizes?.[size.name] || 0}
                                                                        onChange={e =>
                                                                            handleSpecificationPriceChange(
                                                                                tierIndex,
                                                                                "sizes",
                                                                                size.name,
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Finish Options Prices */}
                                                {specifications.finishOptions.length > 0 && (
                                                    <div className="mb-4">
                                                        <label className="block text-xs font-medium text-gray-600 mb-2">Finish Options</label>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                            {specifications.finishOptions.map((finish) => (
                                                                <div key={finish.name} className="flex items-center gap-2">
                                                                    <span className="text-sm text-gray-700 w-32 truncate">{finish.name}</span>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        placeholder="Price"
                                                                        value={tier.specificationPrices?.finishOptions?.[finish.name] || 0}
                                                                        onChange={e =>
                                                                            handleSpecificationPriceChange(
                                                                                tierIndex,
                                                                                "finishOptions",
                                                                                finish.name,
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Corner Options Prices */}
                                                {specifications.cornerOptions.length > 0 && (
                                                    <div className="mb-4">
                                                        <label className="block text-xs font-medium text-gray-600 mb-2">Corner Options</label>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                            {specifications.cornerOptions.map((corner) => (
                                                                <div key={corner.name} className="flex items-center gap-2">
                                                                    <span className="text-sm text-gray-700 w-32 truncate">{corner.name}</span>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        placeholder="Price"
                                                                        value={tier.specificationPrices?.cornerOptions?.[corner.name] || 0}
                                                                        onChange={e =>
                                                                            handleSpecificationPriceChange(
                                                                                tierIndex,
                                                                                "cornerOptions",
                                                                                corner.name,
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Delivery Information */}
                                            <div>
                                                <h5 className="text-sm font-bold text-gray-700 mb-3">Delivery Information</h5>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {/* Standard Delivery */}
                                                    <div className="border border-gray-200 rounded-lg p-3 bg-white">
                                                        <h6 className="text-xs font-bold text-gray-700 mb-2">Standard Delivery</h6>
                                                        <div className="space-y-2">
                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Days</label>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    value={tier.deliveryInfo?.standardDelivery?.days || 3}
                                                                    onChange={e => handleTierDeliveryChange(
                                                                        tierIndex,
                                                                        "standardDelivery",
                                                                        "days",
                                                                        parseInt(e.target.value) || 3
                                                                    )}
                                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Cost (₹)</label>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.01"
                                                                    value={tier.deliveryInfo?.standardDelivery?.cost || 0}
                                                                    onChange={e => handleTierDeliveryChange(
                                                                        tierIndex,
                                                                        "standardDelivery",
                                                                        "cost",
                                                                        parseFloat(e.target.value) || 0
                                                                    )}
                                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Express Delivery */}
                                                    <div className="border border-gray-200 rounded-lg p-3 bg-white">
                                                        <div className="flex justify-between items-center mb-2">
                                                            <h6 className="text-xs font-bold text-gray-700">Express Delivery</h6>
                                                            <label className="flex items-center">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={tier.deliveryInfo?.expressDelivery?.isAvailable ?? true}
                                                                    onChange={e => handleTierDeliveryChange(
                                                                        tierIndex,
                                                                        "expressDelivery",
                                                                        "isAvailable",
                                                                        e.target.checked
                                                                    )}
                                                                    className="rounded border-gray-300"
                                                                />
                                                                <span className="ml-2 text-xs text-gray-600">Available</span>
                                                            </label>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Days</label>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    value={tier.deliveryInfo?.expressDelivery?.days || 1}
                                                                    onChange={e => handleTierDeliveryChange(
                                                                        tierIndex,
                                                                        "expressDelivery",
                                                                        "days",
                                                                        parseInt(e.target.value) || 1
                                                                    )}
                                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Cost (₹)</label>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.01"
                                                                    value={tier.deliveryInfo?.expressDelivery?.cost || 50}
                                                                    onChange={e => handleTierDeliveryChange(
                                                                        tierIndex,
                                                                        "expressDelivery",
                                                                        "cost",
                                                                        parseFloat(e.target.value) || 50
                                                                    )}
                                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Features */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <label className="block text-sm font-medium text-gray-700">Features</label>
                            <button
                                type="button"
                                onClick={addFeature}
                                className="text-sm text-indigo-600 hover:text-indigo-800"
                            >
                                + Add Feature
                            </button>
                        </div>
                        <div className="space-y-2">
                            {formFields.features.map((feature, index) => (
                                <div key={index} className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="Feature description"
                                        value={feature}
                                        onChange={e => handleFeatureChange(index, e.target.value)}
                                        className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeFeature(index)}
                                        className="bg-red-500 text-white px-3 py-2 rounded-md"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>


                    {/* Status */}
                    <div className="flex items-center space-x-4">
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={formFields.isActive}
                                onChange={e => handleInputChange("isActive", e.target.checked)}
                                className="rounded border-gray-300"
                            />
                            <span className="ml-2 text-sm text-gray-700">Active</span>
                        </label>

                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={formFields.isFeatured}
                                onChange={e => handleInputChange("isFeatured", e.target.checked)}
                                className="rounded border-gray-300"
                            />
                            <span className="ml-2 text-sm text-gray-700">Featured</span>
                        </label>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex justify-end space-x-4 pt-8 border-t border-green-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors bg-white/90 backdrop-blur-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-8 py-3 bg-gradient-to-r from-[#008000] to-emerald-600 text-white rounded-xl text-sm font-bold hover:from-[#008000] hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg hover:shadow-xl"
                        >
                            {loading ? (
                                <div className="flex items-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                    Saving...
                                </div>
                            ) : (
                                `${product ? "Update" : "Create"} Product`
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProductManagement;

